/**
 * Карточка загрузки банковской выписки (карточка BL-2026-08-15-2024, §7.1 п. 3).
 *
 * Flow: GET /api/treasury/bank-statements/{id} → шапка файла → по каждому
 *       счёту: итоги «банк vs разбор», инвариант, итоги «нашим взглядом» и
 *       операции счёта (GET …/{id}/entries постранично) → в конце ВСЕГДА
 *       блок «Нераспознанные строки».
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadBankStatement, loadBankStatementEntries, BANK_ENTRIES_PAGE;
 *   - ./BankStatementCommon: названия, бейджи, форматтеры.
 *
 * Связанные файлы:
 *   - src/BankStatementsSection.jsx — открывает карточку по щелчку в журнале;
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md — §3.8 виды операций,
 *     §3.10 инварианты, §6.4–6.5 контракт.
 *
 * # ЧТО КАРТОЧКА ОБЯЗАНА ПОКАЗАТЬ, ДАЖЕ КОГДА ПОКАЗЫВАТЬ «НЕЧЕГО»
 *
 * * Счёт с нулём и без операций — полным блоком с «Операций нет»
 *   (решение стейкхолдера 2026-10-14): пропавший счёт читается как «такого
 *   счёта нет», а не «по нему ничего не было».
 * * Блок «Нераспознанные строки» — всегда: при нуле он говорит «Все строки
 *   распознаны». Это критерий приёмки 1, и молчание о нераспознанном
 *   неотличимо от «не проверяли».
 * * Величина, которую банк не печатает, — прочерк с подсказкой, а не ноль.
 * * Поле, которое сервер скрыл для роли (у viewer `raw_text`, `text`,
 *   `counterparty_*` приходят пустыми, а ответ несёт признак
 *   `personal_data_hidden`), — словом «скрыто», а не пустотой: пустота
 *   читалась бы как «в выписке ничего не было». Признак берётся из ответа:
 *   карточки — для нераспознанных строк, страницы операций — для операций.
 * * Раздел счёта с `account_id = null` (несошедшийся файл по счёту, которого
 *   ещё нет в справочнике) — с пометкой об этом, а не с выдуманной ссылкой.
 */

import { Fragment, useCallback, useEffect, useState } from "react";

import {
  BANK_ENTRIES_PAGE,
  loadBankStatement,
  loadBankStatementEntries,
} from "./api";
import {
  CounterpartyName,
  HiddenValue,
  InvariantBadge,
  KindBadge,
  OUTCOME_TITLES,
  OwnerBadge,
  StatusBadge,
  WORD_FORMS,
  countWord,
  formatAmount,
  formatBytes,
  formatCut,
  formatDate,
  formatMoment,
  formatPeriod,
  isHidden,
  sameDecimal,
} from "./BankStatementCommon";

/** Колонки сверки итогов «банк vs разбор» в порядке выписки. */
export const TOTAL_COLUMNS = [
  { key: "opening", title: "Входящий" },
  { key: "incoming", title: "Приход" },
  { key: "outgoing", title: "Расход" },
  { key: "commission", title: "Комиссия" },
  { key: "closing", title: "Исходящий" },
];

/** Способ определения формата. */
const DETECTION_TITLES = {
  auto: "определён автоматически",
  explicit: "указан при загрузке",
};

/**
 * Расходятся ли «банк» и «разбор» в колонке.
 *
 * @returns {boolean} true — обе величины есть и не равны
 *
 * Если банк величину не даёт (null), расхождения нет: сравнивать не с чем,
 * и подсветка такой ячейки утверждала бы ошибку, которой не было.
 */
export function totalsDiffer(bankValue, parsedValue) {
  if (bankValue === null || bankValue === undefined) return false;
  if (parsedValue === null || parsedValue === undefined) return false;
  return !sameDecimal(bankValue, parsedValue);
}

/**
 * Ячейка итога «банка».
 *
 * У SBM входящий остаток банк не печатает — он восстановлен по первой
 * строке (`opening_source = derived`, §3.10) и показывается курсивом с
 * пометкой, чтобы не читаться цифрой банка.
 */
function BankTotalCell({ column, totals, differ }) {
  const value = totals ? totals[column] : null;
  const cls = `num ${differ ? "bs-diff" : ""}`;
  if (value === null || value === undefined) {
    return (
      <td className={cls} title="банк не даёт эту величину">
        —
      </td>
    );
  }
  if (column === "opening" && totals.opening_source === "derived") {
    return (
      <td className={cls} title="банк не печатает входящий остаток — восстановлен по первой строке">
        <em>{formatAmount(value)}</em> <span className="muted cell-note">восстановлен</span>
      </td>
    );
  }
  return <td className={cls}>{formatAmount(value)}</td>;
}

/**
 * Итоги «нашим взглядом» по видам операций (§3.8).
 *
 * Приход и расход здесь — только `incoming`/`outgoing`; внутренние переводы
 * и обмены — отдельными величинами: сложенные с приходом, они показали бы
 * оборот денег компании там, где деньги лишь переложены между её счетами.
 */
function EconomicLine({ economic, currency }) {
  if (!economic) return null;
  const pair = (a, b) => `+${formatAmount(a)} / −${formatAmount(b)}`;
  return (
    <p className="bs-economic">
      <span className="muted">Нашим взглядом:</span> поступления{" "}
      {formatAmount(economic.incoming, currency)} · списания{" "}
      {formatAmount(economic.outgoing, currency)} · комиссии{" "}
      {formatAmount(economic.fees, currency)} · внутренние{" "}
      {pair(economic.internal_in, economic.internal_out)} · обмены{" "}
      {pair(economic.fx_in, economic.fx_out)}
    </p>
  );
}

/**
 * Перечень проверок инварианта счёта — раскрывается по щелчку.
 */
function ChecksList({ checks }) {
  if (!checks || checks.length === 0) return null;
  const failed = checks.filter((c) => !c.passed).length;
  return (
    <details className="bs-checks" open={failed > 0}>
      <summary>
        Проверки: {checks.length}
        {failed > 0 ? `, не сошлось ${failed}` : ", все сошлись"}
      </summary>
      <table>
        <thead>
          <tr>
            <th>Проверка</th>
            <th className="num">Ожидалось</th>
            <th className="num">Получено</th>
            <th>Итог</th>
          </tr>
        </thead>
        <tbody>
          {checks.map((c, i) => (
            <tr key={`${c.code}-${i}`} className={c.passed ? "" : "bs-check-failed"}>
              <td title={c.code}>
                {c.title}
                {!c.blocking && <span className="muted cell-note"> (не блокирует)</span>}
              </td>
              <td className="num">{formatAmount(c.expected)}</td>
              <td className="num">{formatAmount(c.actual)}</td>
              <td>{c.passed ? "сошлось" : "не сошлось"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

/** Якорь строки операции — для перехода от комиссии к родителю. */
function entryAnchor(statementAccountId, externalKey) {
  return `bs-entry-${statementAccountId}-${externalKey}`;
}

/**
 * Пометки операции: родитель комиссии, вторая нога, торговый счёт клиента,
 * «отправитель — сама компания».
 */
function EntryMarks({ tx, statementAccountId, keysOnPage, onJump }) {
  const marks = [];
  if (tx.parent_external_key) {
    const onPage = keysOnPage.has(tx.parent_external_key);
    marks.push(
      <div key="parent" className="cell-note">
        к операции{" "}
        {onPage ? (
          <button
            type="button"
            className="link"
            onClick={(e) => {
              e.stopPropagation();
              onJump(entryAnchor(statementAccountId, tx.parent_external_key));
            }}
          >
            {tx.parent_external_key}
          </button>
        ) : (
          <span title={tx.parent_transaction_id ? "родитель на другой странице или в другом файле" : "родитель не найден"}>
            {tx.parent_external_key}
          </span>
        )}
        {!tx.parent_transaction_id && tx.transaction_id !== null && (
          <span className="warn"> (не найдена)</span>
        )}
      </div>,
    );
  }
  if (tx.kind === "internal_transfer" || tx.kind === "fx_exchange") {
    marks.push(
      <div key="leg" className="cell-note muted">
        {tx.linked_transaction_id ? "вторая нога найдена" : "вторая нога не найдена"}
      </div>,
    );
  }
  if (tx.client_trading_account) {
    marks.push(
      <div key="client" className="cell-note">
        торговый счёт клиента {tx.client_trading_account}
      </div>,
    );
  }
  if (tx.self_named_counterparty) {
    marks.push(
      <div key="self" className="cell-note warn">
        отправитель — сама компания, счёт не назван
      </div>,
    );
  }
  return marks;
}

/**
 * Операции одного счёта загрузки (§6.5), страницами по 200.
 *
 * @param {number} uploadId — загрузка
 * @param {object} account — раздел счёта из карточки
 */
export function EntriesTable({ uploadId, account }) {
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openRaw, setOpenRaw] = useState(null);
  const [highlight, setHighlight] = useState("");

  const sid = account.statement_account_id;

  useEffect(() => {
    let alive = true;
    loadBankStatementEntries(uploadId, sid, page)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError("");
      })
      .catch((e) => {
        if (!alive) return;
        setError(e.message);
        console.error("операции счёта не загрузились", uploadId, sid, e);
      });
    return () => {
      alive = false;
    };
  }, [uploadId, sid, page]);

  const jump = useCallback((anchor) => {
    setHighlight(anchor);
    const el = document.getElementById(anchor);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, []);

  if (error) return <p className="error">Операции не загрузились: {error}</p>;
  if (!data) return <p className="muted">Загрузка операций…</p>;

  const entries = data.entries || [];
  const total = data.total || 0;
  // Признак скрытия — на уровне ответа страницы, а не операции (§6.5).
  const hidden = Boolean(data.personal_data_hidden);
  if (entries.length === 0 && page === 0) {
    return <p className="muted">Операций нет.</p>;
  }

  const keysOnPage = new Set(
    entries.map((e) => e.transaction && e.transaction.external_key).filter(Boolean),
  );
  const from = page * BANK_ENTRIES_PAGE;

  return (
    <>
      <div className="scroll-rows">
        <table className="bs-table bs-entries">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Контрагент</th>
              <th>Назначение</th>
              <th className="num">Сумма</th>
              <th>Вид</th>
              <th>Связи и пометки</th>
              <th>Исход</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const tx = e.transaction || {};
              const anchor = tx.external_key ? entryAnchor(sid, tx.external_key) : undefined;
              const rowKey = `${e.line_no}`;
              const cls = [
                "bs-row-click",
                e.outcome === "conflict" ? "bs-row-conflict" : "",
                e.outcome === "not_promoted" ? "bs-row-not-promoted" : "",
                anchor && anchor === highlight ? "bs-row-selected" : "",
              ].join(" ");
              return (
                <Fragment key={rowKey}>
                  <tr
                    id={anchor}
                    className={cls}
                    onClick={() => setOpenRaw(openRaw === rowKey ? null : rowKey)}
                    title="показать строку выписки как есть"
                  >
                    <td>
                      {formatDate(tx.booking_date)}
                      {tx.occurred_at && (
                        <div className="cell-note muted">{formatMoment(tx.occurred_at).slice(11)}</div>
                      )}
                    </td>
                    <td>
                      <CounterpartyName tx={tx} hidden={hidden} />
                      {tx.counterparty_account && (
                        <div className="cell-note muted">{tx.counterparty_account}</div>
                      )}
                    </td>
                    <td className="bs-purpose">{tx.purpose}</td>
                    <td className="num">
                      {formatAmount(tx.signed_amount, tx.currency)}
                      {e.conflict && (
                        <div className="cell-note warn">
                          в файле: {e.conflict.direction === "out" ? "−" : "+"}
                          {formatAmount(e.conflict.amount, e.conflict.currency)},{" "}
                          {formatDate(e.conflict.booking_date)}
                        </div>
                      )}
                    </td>
                    <td>
                      <KindBadge
                        kind={tx.kind}
                        title={tx.kind_title}
                        feeKindTitle={tx.kind === "fee" ? tx.fee_kind_title : ""}
                      />
                    </td>
                    <td>
                      <EntryMarks
                        tx={{ ...tx, transaction_id: tx.id ?? null }}
                        statementAccountId={sid}
                        keysOnPage={keysOnPage}
                        onJump={jump}
                      />
                    </td>
                    <td>{e.outcome_title || OUTCOME_TITLES[e.outcome] || e.outcome}</td>
                  </tr>
                  {openRaw === rowKey && (
                    <tr className="bs-raw-row">
                      <td colSpan={7}>
                        {isHidden(tx.raw_text, hidden) ? (
                          <p>
                            Текст строки: <HiddenValue />
                          </p>
                        ) : (
                          <pre className="bs-mono">{tx.raw_text}</pre>
                        )}
                        <span className="muted cell-note">
                          стр. {e.page}, строка текста {e.source_line} · ключ {tx.external_key}
                          {tx.classified_by ? ` · правило ${tx.classified_by}` : ""}
                        </span>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {total > BANK_ENTRIES_PAGE && (
        <p className="muted">
          Операции {from + 1}–{from + entries.length} из {total}.{" "}
          <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>
            Назад
          </button>{" "}
          <button
            type="button"
            disabled={from + entries.length >= total}
            onClick={() => setPage(page + 1)}
          >
            Вперёд
          </button>
        </p>
      )}
    </>
  );
}

/**
 * Блок одного счёта в загрузке.
 *
 * @param {number} uploadId — загрузка
 * @param {object} account — раздел счёта из карточки (§6.2 accounts[])
 */
export function StatementAccountBlock({ uploadId, account }) {
  const bank = account.bank_totals || {};
  const parsed = account.parsed_totals || {};
  return (
    <section className={`bs-account ${account.funds_owner === "client" ? "bs-account-client" : ""}`}>
      <h3>
        {account.account_number} · {account.currency}{" "}
        <OwnerBadge owner={account.funds_owner} />{" "}
        <InvariantBadge invariant={account.invariant} />
      </h3>
      {account.account_id === null && (
        // Не ошибка разбора: счёт впервые встретился в несошедшемся файле,
        // и в справочник его заведёт первая принятая выписка (находка M-1).
        <p className="cell-note warn">
          Счёт ещё не заведён в справочник банковских счетов — операции по нему
          появятся там после первой принятой выписки.
        </p>
      )}
      <p className="muted">
        {account.holder_name} · период {formatPeriod(account.period_from, account.period_to)} ·
        срез {formatCut(account.cut_at, account.cut_precision)} ·{" "}
        {countWord(account.operations, WORD_FORMS.operation)}
        {" "}(новых {account.operations_new}, уже были {account.operations_known}
        {account.operations_conflict > 0 ? `, конфликтов ${account.operations_conflict}` : ""})
        {account.unrecognized > 0
          ? ` · ${countWord(account.unrecognized, WORD_FORMS.unrecognizedLine)}`
          : ""}
      </p>

      <table className="bs-totals">
        <thead>
          <tr>
            <th />
            {TOTAL_COLUMNS.map((c) => (
              <th key={c.key} className="num">
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Банк</th>
            {TOTAL_COLUMNS.map((c) => (
              <BankTotalCell
                key={c.key}
                column={c.key}
                totals={bank}
                differ={totalsDiffer(bank[c.key], parsed[c.key])}
              />
            ))}
          </tr>
          <tr>
            <th scope="row">Разбор</th>
            {TOTAL_COLUMNS.map((c) => (
              <td
                key={c.key}
                className={`num ${totalsDiffer(bank[c.key], parsed[c.key]) ? "bs-diff" : ""}`}
              >
                {formatAmount(parsed[c.key])}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      {bank.header_balance && (
        <p className="muted cell-note">
          Остаток в шапке выписки (на момент выгрузки): {formatAmount(bank.header_balance, account.currency)}
        </p>
      )}

      <ChecksList checks={account.checks} />
      <EconomicLine economic={account.economic} currency={account.currency} />

      {account.operations === 0 ? (
        <p className="muted">Операций нет.</p>
      ) : (
        <EntriesTable uploadId={uploadId} account={account} />
      )}
    </section>
  );
}

/**
 * Блок «Нераспознанные строки» — ВСЕГДА виден (§7.1 п. 3).
 *
 * @param {object[]} lines — нераспознанные строки карточки (§6.2)
 * @param {boolean} truncated — сервер отдал не все строки
 * @param {boolean} [hidden] — признак `personal_data_hidden` карточки:
 *        пустой текст строки — скрыт для роли, а не пуст в выписке
 */
export function UnrecognizedBlock({ lines, truncated, hidden = false }) {
  return (
    <section className="bs-unrecognized">
      <h3>Нераспознанные строки</h3>
      {lines.length === 0 ? (
        <p className="muted">Все строки распознаны.</p>
      ) : (
        <>
          {truncated && (
            <p className="warn">
              Показаны первые {countWord(lines.length, WORD_FORMS.line)} — остальные не выведены.
            </p>
          )}
          <table className="bs-table">
            <thead>
              <tr>
                <th className="num">Стр.</th>
                <th className="num">Строка</th>
                <th>Текст</th>
                <th>Причина</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td className="num">{l.page}</td>
                  <td className="num">{l.source_line}</td>
                  <td>
                    {isHidden(l.text, hidden) ? (
                      <HiddenValue />
                    ) : (
                      <div className="bs-mono bs-nowrap">{l.text}</div>
                    )}
                  </td>
                  <td>
                    {l.reason_title || l.reason}
                    {l.reason_detail && <div className="cell-note muted">{l.reason_detail}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

/**
 * Шапка загрузки: файл, хэши, разборщик, кто и когда, предупреждения.
 */
function UploadHeader({ upload, duplicateOf, onOpen }) {
  const u = upload;
  return (
    <>
      <h2>
        Загрузка №{u.id} <StatusBadge status={u.status} title={u.status_title} />
      </h2>
      <dl className="bs-head">
        <dt>Файл</dt>
        <dd>
          {u.file_name} · {formatBytes(u.file_size)}
          {u.file_stored === false && <span className="warn"> · оригинал не сохранён</span>}
        </dd>
        <dt>md5</dt>
        <dd>
          <input className="bs-mono bs-copy" readOnly value={u.md5 || ""} aria-label="md5" onFocus={(e) => e.target.select()} />
        </dd>
        <dt>sha256</dt>
        <dd className="bs-mono" title={u.sha256}>
          {u.sha256 ? `${u.sha256.slice(0, 16)}…` : "—"}
        </dd>
        <dt>Разборщик</dt>
        <dd>
          {u.bank_title || u.bank || "—"}
          {u.parser_version ? ` · ${u.parser_version}` : ""}
          {u.format_detection ? ` · ${DETECTION_TITLES[u.format_detection] || u.format_detection}` : ""}
        </dd>
        <dt>Загрузил</dt>
        <dd>
          {u.uploaded_by} · {formatMoment(u.uploaded_at)}
        </dd>
        <dt>Сформирована банком</dt>
        <dd>{u.generated_at ? formatMoment(u.generated_at) : "банк не указал"}</dd>
        <dt>Период и срез</dt>
        <dd>
          {formatPeriod(u.period_from, u.period_to)} · срез {formatCut(u.cut_at, u.cut_precision)}
          {u.cut_precision === "day" ? " (с точностью до дня)" : ""}
        </dd>
      </dl>

      {u.status === "duplicate" && (
        <p className="notice">
          Тот же файл уже принят
          {duplicateOf ? ` ${formatMoment(duplicateOf.uploaded_at)} пользователем ${duplicateOf.uploaded_by}` : ""}
          {" "}— загрузка №{u.duplicate_of_id}.{" "}
          <button type="button" className="link" onClick={() => onOpen(u.duplicate_of_id)}>
            Открыть исходную
          </button>
        </p>
      )}
      {u.status === "failed" && u.error && <p className="error">{u.error}</p>}
      {u.status === "invariant_failed" && (
        <p className="error">
          Итоги банка не сошлись с разбором хотя бы по одному счёту — в учёт не
          принято ничего: ни операции, ни остатки. Операции ниже показаны как
          разобраны, с исходом «Не принята».
        </p>
      )}
      {(u.warnings || []).length > 0 && (
        <div className="incomplete">
          <strong className="warn">Предупреждения</strong>
          <ul>
            {u.warnings.map((w, i) => (
              <li key={`${w.code}-${i}`} title={w.code}>
                {w.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/**
 * Карточка загрузки.
 *
 * @param {number} id — загрузка
 * @param {function(number): void} onOpen — открыть другую загрузку (дубликат)
 * @param {function(): void} onClose — закрыть карточку
 */
export default function BankStatementCard({ id, onOpen, onClose }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setCard(null);
    setError("");
    loadBankStatement(id)
      .then((d) => alive && setCard(d))
      .catch((e) => {
        if (!alive) return;
        setError(e.message);
        console.error("карточка загрузки не загрузилась", id, e);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const close = onClose ? (
    <button type="button" className="link bs-close" onClick={onClose}>
      закрыть
    </button>
  ) : null;

  if (error) {
    return (
      <section className="card bs-card">
        <h2>Загрузка №{id}</h2>
        {close}
        <p className="error">{error}</p>
      </section>
    );
  }
  if (!card) {
    return (
      <section className="card bs-card">
        <h2>Загрузка №{id}</h2>
        <p className="muted">Загрузка…</p>
      </section>
    );
  }

  const accounts = card.accounts || [];
  // Клиентские счета — после собственных и отдельным заголовком: деньги на
  // них не ликвидность компании (решение 2026-10-14), и в одном ряду с
  // собственными их суммы складывались бы в уме.
  const own = accounts.filter((a) => a.funds_owner !== "client");
  const client = accounts.filter((a) => a.funds_owner === "client");

  return (
    <section className="card bs-card">
      {close}
      <UploadHeader upload={card.upload} duplicateOf={card.duplicate_of} onOpen={onOpen} />

      {accounts.length === 0 && card.upload.status !== "duplicate" && card.upload.status !== "failed" && (
        <p className="muted">Разделов счетов в выписке нет.</p>
      )}
      {own.map((a) => (
        <StatementAccountBlock key={a.statement_account_id} uploadId={card.upload.id} account={a} />
      ))}
      {client.length > 0 && (
        <div className="bs-client-group">
          <h3>Клиентские счета — не ликвидность компании</h3>
          {client.map((a) => (
            <StatementAccountBlock key={a.statement_account_id} uploadId={card.upload.id} account={a} />
          ))}
        </div>
      )}

      <UnrecognizedBlock
        lines={card.unrecognized || []}
        truncated={Boolean(card.unrecognized_truncated)}
        hidden={Boolean(card.personal_data_hidden)}
      />
    </section>
  );
}
